import { extend } from "./core";
import { el } from "./dom";
import { getScrollbarWidth, isIE } from "./html";
import { IHandlers } from "./types";

interface IScrollViewConfig {
	speed: number;
}

export class ScrollView {
	public config: IScrollViewConfig;

	private _getRootView;

	private _scrollTop: number;
	private _runnerTop: number;
	private _runnerHeight: number;

	private _visibleArea: number;

	private _scrollWidth: number;

	private _wheelName: string;

	private _handlers: IHandlers;

	constructor(getRootView, config = {}) {
		this.config = extend({
			speed: 20
		}, config);

		this._wheelName = isIE() ? "onmousewheel" : "onwheel";

		this._getRootView = getRootView;
		this._scrollTop = 0;
		this._runnerTop = 0;
		this._runnerHeight = 0;

		this._visibleArea = 1;

		this._scrollWidth = getScrollbarWidth();
		this._handlers = {
			onscroll: () => {
				this._update();
			},
			[this._wheelName]: e => {
				e.preventDefault();

				const sign = (e.deltaY || -e.wheelDelta) > 0 ? 1 : -1;

				const delta = sign * this.config.speed;

				const {area} = this._getRefs();
				const maxBottom = area.scrollHeight - this._runnerHeight;

				const newScrollTop = this._scrollTop + delta;

				if (newScrollTop < 0) {
					area.scrollTop = 0;
				} else if (newScrollTop > maxBottom) {
					area.scrollTop = maxBottom;
				} else {
					area.scrollTop = newScrollTop;
				}

				this._update();
			},
			onmousedownRunner: mouseDownEv => {
				mouseDownEv.preventDefault();

				const {area, runner} = this._getRefs();

				const rect = area.getBoundingClientRect();
				const top = rect.top + window.pageYOffset;
				const bottom = rect.bottom + window.pageYOffset;
				const maxBottom = area.scrollHeight - this._runnerHeight;

				const delta = mouseDownEv.pageY - runner.getBoundingClientRect().top - window.pageYOffset;

				const mouseMove = (e: MouseEvent) => {
					const y = e.pageY - delta;

					if (y <= top) {
						area.scrollTop = 0;
					} else if (y > bottom) {
						area.scrollTop = maxBottom;
					} else {
						area.scrollTop = (y - top) / this._visibleArea;
					}

					this._update();
				};
				const mouseUp = () => {
					document.removeEventListener("mousemove", mouseMove);
					document.removeEventListener("mouseup", mouseUp);
					document.body.classList.remove("dhx-no-select");
				};

				document.body.classList.add("dhx-no-select");
				document.addEventListener("mousemove", mouseMove);
				document.addEventListener("mouseup", mouseUp);
			},
			onmousedownTrack: e => {
				if (e.target.classList.contains("scroll-runner")) {
					return;
				}
				e.preventDefault();

				const mouseUp = () => {
					document.removeEventListener("mouseup", mouseUp);
					window.clearInterval(mousePressed as any); // typescript bug
				};

				const {area} = this._getRefs();
				const top = e.target.getBoundingClientRect().top + window.pageYOffset;

				const maxBottom = area.scrollHeight - this._runnerHeight;
				const y = e.pageY;

				const updateScroll = () => {
					let scrollTop;

					if (y < top + this._runnerTop) {
						scrollTop = this._scrollTop - area.clientHeight;

						if (scrollTop < 0) {
							scrollTop = 0;
						}
					} else if (y > top + this._runnerTop + this._runnerHeight) {
						scrollTop = this._scrollTop + area.clientHeight;

						if (scrollTop > maxBottom) {
							scrollTop = maxBottom;
						}
					} else {
						return;
					}

					area.scrollTop = scrollTop;
					this._update();
				};

				updateScroll();
				const mousePressed = setInterval(updateScroll, 100);
				document.addEventListener("mouseup", mouseUp);
			}
		};
	}
	render(element) {
		if (this._scrollWidth === 0) {
			return element;
		}
		return el(".scroll-view-wrapper", {
			style: {
				width: "100%",
				height: "100%",
				overflow: "hidden",
				position: "relative"
			}
		}, [
			el(".scroll-view", {
				onscroll: this._handlers.onscroll,
				_ref: "scroll-view",
				_hooks: {
					didInsert: () => {
						this._update();
					},
					didRecycle: () => {
						this._update();
					}
				},
				style: {
					"height": "100%",
					"width": `calc(100% + ${this._scrollWidth}px)`,
					"overflowY": "scroll",
					"-ms-overflow-style": "scrollbar"
				},
			}, [element]),
			el(".y-scroll", {
				onmousedown: this._handlers.onmousedownTrack,
				[this._wheelName]: this._handlers[this._wheelName],
				style: {
					width: "10px",
					height: "100%",
					right: 0,
					top: 0,
					position: "absolute"
				}
			}, [
				el(".scroll-runner", {
					_ref: "scroll-runner",
					onmousedown: this._handlers.onmousedownRunner,
					style: {
						height: this._runnerHeight + "px",
						right: "2px",
						top: this._runnerTop,
						width: "6px",
						position: "absolute"
					}
				})
			])
		]);
	}
	private _update() {
		const refs = this._getRefs();
		if (!refs) {
			return;
		}

		const {area, runner} = refs;

		this._visibleArea = area.clientHeight / area.scrollHeight;
		this._scrollTop = area.scrollTop;
		this._runnerTop =  this._scrollTop * this._visibleArea;

		if (this._visibleArea < 1) {
			this._runnerHeight = area.clientHeight * this._visibleArea;
		} else {
			this._runnerHeight = 0;
		}

		// update dom
		runner.style.top = this._runnerTop + "px";
		runner.style.height = this._runnerHeight + "px";
	}
	private _getRefs() {
		const rootView = this._getRootView();
		if (rootView.refs && rootView.refs["scroll-view"] && rootView.refs["scroll-runner"]) {
			return {
				area: rootView.refs["scroll-view"].el,
				runner: rootView.refs["scroll-runner"].el
			};
		}
	}
}